import React, { ReactNode, useMemo, useRef, useState } from 'react';
import {
  AvDateRangePicker,
  booleanOptions,
  ConditionType,
  defaultOperator,
  epochDateString,
  FeatureFlags,
  Filter,
  filterOperatorOptions,
  FilterStyledPopper,
  getRootOperator,
  isDateRangeEmpty,
  isDateRangeNotEmpty,
  isDateRangeValue,
  isNullOperator,
  operatorColors,
  operatorLabels,
  OperatorType,
  PeriodBehavior,
  RadioButton,
  SelectTrigger,
} from '@fabric-ui/components';
import { AsyncGetOptions, OptionQueryType } from '@fabric-ui/components/Table';
import { emptyArray, getIfValidStringifiedObject, noop, uniqBy } from '@fabric-ui/utils';
import { Box, ClickAwayListener, Divider } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { add, addDays, addMonths, addWeeks, format, isEqual, subDays, subMonths, subWeeks } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../context/AvContextProvider';
import { useAsyncFilterOptions } from '../hooks/useAsyncFilterOptions';
import { useDebounceV1 } from '../hooks/useDebounce';
import {
  cleanEmptyFilters,
  filterToExpression,
  isAdvancedFilterMode,
  isConditionFilterMode,
  isListFilterMode,
  sortBySeverity,
} from '../utils/filterUtils';
import { generateOption, getEscapedValue, getNestedValue, isJSON, orderByKey, setNestedValue } from '../utils/utils';
import { DwQueryRequest, SortDir } from '../views/CustomDashboards/types';
import { useMeasurementsList } from '../views/Reports/hooks';
import { SchemaTypes, typeLabelMap } from '../views/Sources/Mapping/types';
import { useBuildWhereClause } from '../views/Tickets/hooks';
import FilterActionButtons from './filters/components/FilterActionButtons';
import FilterByAdvanced from './filters/components/FilterByAdvanced';
import { FilterByCondition } from './filters/components/FilterByCondition';
import { FilterByList } from './filters/components/FilterByList';

interface FieldFilterProps {
  activeProjName?: string;
  field: string;
  label?: ReactNode;
  filters?: Record<string, any>;
  isEnabled: boolean;
  onChange: (field, value) => void;
  onFilterChange?: (filter: Filter) => void;
  onOpen?: () => void;
  onClose?: () => void;
  setRef?: (ref: any) => void;
  getOptions?: (data: any, label: ReactNode, v) => any[];
  overrideExtra?: string[];
  size?: string;
  disabled?: boolean;
  trigger?: any;
  fieldType?: keyof typeof typeLabelMap;
  options?: any[];
  isMultipleCondition?: boolean;
  fieldMap?: Record<string, any>;
  queryKey?: string;
  getValue?: (v) => ReactNode;
  isFilterExpression?: boolean;
  usage?: 'SPF' | undefined;
  isGlobalFilter?: boolean;
  selectIcon?: ReactNode;
  enableQueryObj?: boolean;
  onSearchChange?: (search: string) => void;
  asyncGetOptions?: AsyncGetOptions;
  extraOperands?: Filter[];
  isIngressField?: boolean;
  disablePortalInOperatorSelect?: boolean;
  isHistorical?: boolean;
  hideAdvanced?: boolean;
}

const getDisplayValue = value => (isJSON(value) ? (value.from || value.to ? `${value.from || ''}-${value.to || ''}` : '') : value);
const getField = (field: unknown): Array<unknown> | undefined => (field ? (Array.isArray(field) ? field : [field]) : undefined);

export const FieldFilter: React.FC<FieldFilterProps> = ({
  onChange,
  onFilterChange,
  field,
  filters = {},
  isEnabled,
  setRef = noop,
  getOptions = (data, label) =>
    data.map(({ value }) => (value === null || value?.length === 0 ? { title: `No ${label}`, value: null } : generateOption(value))),
  label = '',
  overrideExtra,
  size = 'small',
  disabled,
  trigger,
  fieldType,
  options,
  isMultipleCondition = true,
  onOpen = noop,
  onClose = noop,
  activeProjName,
  queryKey,
  getValue,
  isFilterExpression = false,
  usage,
  isGlobalFilter = false,
  selectIcon,
  enableQueryObj = false,
  asyncGetOptions,
  extraOperands,
  isIngressField,
  disablePortalInOperatorSelect = true,
  isHistorical,
  hideAdvanced = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    featureFlags,
    accountEntities: { aggProjs, fieldMap },
  } = useAvContext();
  const typeBase =
    fieldType || typeLabelMap[fieldMap[field]?.type as string | number] || (field.toLowerCase().endsWith('count') ? 'number' : 'string');
  const type = `${typeBase}${fieldMap[field]?.repeated ? '[]' : ''}`;
  const isSchemaField = !options;
  const isBooleanField = type === 'bool';
  const isDateField = ['date', SchemaTypes.DATETIME].includes(type);
  const supportedType = !['["MESSAGE"]', 'MESSAGE'].includes(fieldMap?.[field]?.type?.toString());
  const isSeverity = field.split('.')[1] === 'severity';
  const isNumberField = type === 'number';
  const filterObject = getIfValidStringifiedObject(filters[field]);
  const shouldShowMinutesAndHours = isDateField && fieldsToIncludeMinutesAndHours.has(field);

  const [advancedFilterValue, setAdvancedFilterValue] = useState(() => {
    if (
      filterObject?.type !== undefined ||
      filterObject?.dir !== undefined ||
      filterObject?.top !== undefined ||
      filterObject?.value !== undefined
    ) {
      return { type: filterObject.type, dir: filterObject.dir, top: filterObject.top, value: filterObject.value };
    }
    return {};
  });

  const [mode, setMode] = useState(() => {
    if (filterObject) {
      if (filterObject?.type !== undefined || filterObject?.top !== undefined || filterObject?.byValue !== undefined) {
        return 'advanced';
      }
      return 'condition';
    }
    return 'list';
  });

  // TODO: Remove when AVA-12635 is implemented.
  // When null isn't first in the array it is parsed incorrectly
  const value = isAdvancedFilterMode(mode) ? [] : filterObject || getField(filters[field])?.map(val => (val === 'null' ? null : val)) || [];

  const initialFilter = isAdvancedFilterMode(mode) ? {} : emptyOperand[type];
  const [conditionValue, setConditionValue] = useState(filterObject ? value : { [isNumberField ? 'or' : 'and']: [initialFilter] });
  const operator = getRootOperator<OperatorType>(conditionValue);
  const valueOperator = getRootOperator(filterObject);
  const buttonRef = useRef(null);

  const [errors, setErrors] = useState<typeof conditionValue>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedValue = useDebounceV1(search, 500);
  const escapedValue = getEscapedValue(debouncedValue);
  const searchCondition = debouncedValue ? `value LIKE '%${escapedValue}%'` : undefined;

  const handleSelectConditionChange = (value: unknown, index: number) => {
    setErrors([]);
    setConditionValue(prev => {
      const nestedPrev = getNestedValue(`${operator}.${index}`, prev);
      const newValue = isNullOperator(value) ? '' : nestedPrev.value;
      return setNestedValue(`${operator}.${index}`, prev, { operator: value, value: newValue });
    });
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleAdvancedFilterChange = newAdvancedFilterValue => {
    setAdvancedFilterValue(newAdvancedFilterValue);
    onChange(field, [encodeURIComponent(JSON.stringify(newAdvancedFilterValue))]);
  };

  const whereClause =
    isSchemaField && activeProjName
      ? useBuildWhereClause({
          filters: isGlobalFilter ? { [field]: [] } : { ...filters, [field]: [] },
          // @ts-ignore
          extra: overrideExtra ? [...overrideExtra, searchCondition] : searchCondition,
          entityTypeId: (isGlobalFilter ? fieldMap[field] : aggProjs[activeProjName])?.entityTypeId?.name,
          fieldMap,
        })
      : '';

  const finalWhereClause = useMemo(
    () => whereClause,
    [isSchemaField, activeProjName, fieldMap, filters, field, searchCondition, overrideExtra, isGlobalFilter, aggProjs]
  );

  const getFieldQuery = field => {
    const fieldQuery = isGlobalFilter ? `${activeProjName}.${field.split('.').slice(1).join('.')}` : field;
    const explodeField = fieldMap?.[field]?.repeated ? `EXPLODE(${fieldQuery})` : fieldQuery;
    return isIngressField && activeProjName
      ? explodeField.replace(aggProjs[activeProjName].pathAlias, `${aggProjs[activeProjName]?.pathAlias}.sources`)
      : explodeField;
  };

  const fieldQuery = getFieldQuery(field);
  const sql = `SELECT distinct ${fieldQuery} as value
    FROM ${activeProjName}
    ${finalWhereClause} ORDER BY value ASC limit 800`;

  const { data: measurements = [] } = useMeasurementsList({ entityName: aggProjs[activeProjName ?? '']?.entityTypeId?.name });

  const typeMap = useMemo(
    () => measurements.reduce((acc, { systemName, type }) => ({ ...acc, [systemName]: type }), aggProjs[activeProjName ?? '']?.typeMap),
    [measurements, activeProjName]
  );

  const fieldQueryObj = useMemo(() => {
    const filtersObj = typeMap
      ? cleanEmptyFilters(filterToExpression(typeMap, isGlobalFilter ? { [field]: [] } : { ...filters, [field]: [] }))
      : undefined;
    return {
      select: { dims: [{ name: fieldQuery, alias: 'value' }], metrics: [] },
      filter:
        activeProjName && (escapedValue || filtersObj || extraOperands)
          ? {
              and: {
                operands: [
                  ...(escapedValue ? [{ expression: { fieldName: 'value', stringCondition: { contains: escapedValue } } }] : []),
                  ...(filtersObj ? [filtersObj] : []),
                  ...(extraOperands || []),
                ],
              },
            }
          : undefined,
      top: { size: 800, groupOthers: false },
      sorting: [{ name: 'value', dir: SortDir.ASC }],
      ...(activeProjName ? { projectionId: aggProjs[activeProjName]?.projId } : {}),
      distinct: true,
    };
  }, [fieldQuery, activeProjName, typeMap, isGlobalFilter, filters, escapedValue]);

  const queryObjectOptions = {
    options: {
      type: OptionQueryType.Object,
      queryObject: fieldQueryObj as DwQueryRequest,
      debounceQueries: false,
      onSuccess: data => sortBySeverity({ data, severityFieldName: fieldQuery, key: 'value' }),
      totalRowCount: true,
    },
    isEnabled:
      isEnabled &&
      isListFilterMode(mode) &&
      open &&
      !isDateField &&
      !isBooleanField &&
      supportedType &&
      !isNumberField &&
      isSchemaField &&
      enableQueryObj,
    placeholderData: keepPreviousData,
  };

  const querySqlOptions = {
    options: {
      type: OptionQueryType.SQL,
      key: queryKey ?? sql,
      sql,
      debounceQueries: false,
      placeholderData: keepPreviousData,
      onSuccess: data => sortBySeverity({ data, severityFieldName: fieldQuery, key: 'value' }),
      usage,
      showTotals: true,
    },
    isEnabled:
      isEnabled &&
      isListFilterMode(mode) &&
      open &&
      !isDateField &&
      !isBooleanField &&
      supportedType &&
      !isNumberField &&
      isSchemaField &&
      !enableQueryObj,
  };

  const asyncFilterOptions = useMemo(
    () =>
      asyncGetOptions
        ? {
            options: asyncGetOptions?.(escapedValue),
            isEnabled: open,
          }
        : undefined,
    [asyncGetOptions, escapedValue, open]
  );
  const {
    data: filterOptions = emptyArray,
    isLoading: isLoadingOptions,
    totalRowCount,
  } = useAsyncFilterOptions(asyncFilterOptions || (enableQueryObj ? queryObjectOptions : querySqlOptions));

  const memoOptions = useMemo(
    () =>
      options
        ? orderByKey(
            options.filter(o => (isBooleanField ? true : o.value?.toLowerCase?.().includes(search.toLowerCase()))),
            'title'
          )
        : uniqBy(
            getOptions([...(value.map?.(generateOption) || []), ...filterOptions], label, getValue).map(({ title, value }) => ({
              value,
              title: value === null ? 'Empty' : title,
            })),
            f => f.value
          ),
    [filterOptions, open, search, options]
  );

  const tooltipValue = useMemo(
    () =>
      filterObject && !isAdvancedFilterMode(mode) ? (
        isDateField ? (
          <div>{formatDateValue({ ...filterObject })}</div>
        ) : (
          <div>{filterObject[valueOperator].map((operand, index, arr) => conditionTooltip(operand, index, arr, operator))}</div>
        )
      ) : (
        value?.join(', ')
      ),
    [value, conditionValue, filterObject, operator, getValue]
  );
  const calcOptions = field === 'finding.state' ? findingStateOptions : isBooleanField && isSchemaField ? booleanOptions : memoOptions;

  const viewOptions = useMemo(() => {
    const options = [
      { title: 'List', value: 'list' },
      { title: 'Condition', value: 'condition' },
    ];
    if (featureFlags[FeatureFlags.TopK] && !hideAdvanced) {
      options.push({ title: 'Advanced', value: 'advanced' });
    }
    return options;
  }, [featureFlags, hideAdvanced]);

  const getErrors = (val, operator) =>
    !operator ? ['Select Operator'] : filterOperatorOptions[type].find(({ value }) => value === operator)?.getErrors?.(val);

  const isSelectTrigger = !trigger || trigger.type.name === 'Select';
  const displayValue = useMemo(
    () =>
      filterObject
        ? isDateField
          ? formatDateValue({ ...filterObject })
          : `${operatorLabels[filterObject[valueOperator][0]?.operator]}  ${getDisplayValue(filterObject[valueOperator][0]?.value)}`
        : getValue && value[0] !== getValue(value[0])
          ? getValue(value[0])
          : calcOptions.find(o => o.value === value[0])?.title || `${value}`,
    [value, conditionValue, filterObject, operator, getValue]
  );

  const triggerProps = {
    ref: buttonRef,
    ...(isSelectTrigger
      ? {
          hasValidValue: Boolean(value?.length || filterObject[valueOperator]?.length > 0 || filterObject?.value !== undefined),
          processedValue: value,
          displayValue,
          tooltipValue,
          chipCount: isDateField
            ? undefined
            : filterObject
              ? conditionValue[operator].length - 1
              : value.length
                ? value.length - 1
                : undefined,
        }
      : {}),
    open,
    label: label as React.ReactNode,
    size: size as 'small' | 'xSmall' | 'medium',
    disabled,
    selectIcon,
    onClick: () => {
      setOpen(true);
      onOpen();
      if (!supportedType) {
        enqueueSnackbar('Unsupported Filter Type', { variant: 'error' });
      }
    },
  } as const;

  const isTextField = !isNumberField && !isDateField;
  const innerContent = isDateField ? (
    <AvDateRangePicker
      value={filterObject.value}
      activePresetValue={filterObject.preset}
      onChange={value => {
        if (value.value === undefined) {
          onChange(field, []);
          return;
        }
        if (isDateRangeEmpty(value.value) || value.value === null) {
          onChange(field, [encodeURIComponent(JSON.stringify({ value: 'empty' }))]);
          return;
        }
        if (isDateRangeNotEmpty(value.value)) {
          onChange(field, [encodeURIComponent(JSON.stringify({ value: 'notEmpty' }))]);
          return;
        }
        onChange(field, [encodeURIComponent(JSON.stringify(value))]);
      }}
      onClose={handleClose}
      showAsCalendar
      isRange
      showRelativeOptions
      isFilterExpression={isFilterExpression}
      showMinutesAndHours={shouldShowMinutesAndHours}
    />
  ) : (
    <>
      {initialFilter && !isNumberField && (
        <Box sx={{ py: 1, px: 2 }}>
          <RadioButton options={viewOptions} value={mode} onChange={setMode} />
        </Box>
      )}
      <Divider />

      {(isTextField || isBooleanField) && isListFilterMode(mode) && (
        <FilterByList
          label={label}
          isLoadingOptions={isLoadingOptions}
          isSeverity={isSeverity}
          isTextField={isTextField}
          options={calcOptions}
          value={filterObject ? [] : isBooleanField ? value?.map(JSON.parse) : value}
          onChange={onChange}
          field={field}
          type={type}
          onFilterChange={onFilterChange}
          totalOptionsCount={totalRowCount}
          isSchemaField={isSchemaField}
          setSearch={setSearch}
        />
      )}

      {(isConditionFilterMode(mode) || !isTextField) && (
        <FilterByCondition
          initialFilter={initialFilter}
          conditionValue={conditionValue}
          operator={operator}
          isMultipleCondition={isMultipleCondition}
          disablePortalInOperatorSelect={disablePortalInOperatorSelect}
          type={type}
          handleSelectConditionChange={handleSelectConditionChange}
          isNumberField={isNumberField}
          setErrors={setErrors}
          setConditionValue={setConditionValue}
          onChange={onChange}
          field={field}
          onFilterChange={onFilterChange}
          errors={errors}
          filterOperatorOptions={filterOperatorOptions}
        />
      )}

      {isAdvancedFilterMode(mode) && (
        <FilterByAdvanced
          advancedFilter={advancedFilterValue}
          onChange={handleAdvancedFilterChange}
          isHistorical={isHistorical}
          activeProjName={activeProjName}
        />
      )}

      <Divider orientation="horizontal" />

      {(isConditionFilterMode(mode) || isAdvancedFilterMode(mode) || isNumberField) && (
        <FilterActionButtons
          setErrors={setErrors}
          setConditionValue={setConditionValue}
          operator={operator}
          initialFilter={initialFilter}
          setOpen={setOpen}
          onChange={onChange}
          field={field}
          onFilterChange={onFilterChange}
          conditionValue={conditionValue}
          getErrors={getErrors}
          mode={mode}
          advancedFilterValue={advancedFilterValue}
          setAdvancedFilterValue={setAdvancedFilterValue}
        />
      )}
    </>
  );

  return (
    <span key={field} ref={setRef}>
      {trigger ? React.cloneElement(trigger, triggerProps) : <SelectTrigger {...triggerProps} />}
      <FilterStyledPopper
        open={open}
        anchorEl={buttonRef.current}
        placement="bottom-start"
        popperOptions={{ strategy: 'fixed' }}
        sx={{ minWidth: '400px', width: 'fit-content', padding: 0 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Box style={{ display: open ? 'block' : 'none' }}>{innerContent}</Box>
        </ClickAwayListener>
      </FilterStyledPopper>
    </span>
  );
};

export const conditionTooltip = (operand, index, arr, operator) => (
  <span key={index}>
    <span style={{ fontWeight: 600 }}>{operatorLabels[operand.operator]} </span>
    <span>{getDisplayValue(operand.value)}</span>{' '}
    {index === arr.length - 1 ? (
      ''
    ) : (
      <Box component="span" sx={{ fontWeight: 600, color: operatorColors[operator] }}>
        {operator.toUpperCase()}{' '}
      </Box>
    )}
  </span>
);

const defaultDateFormat = 'MMM dd, yyyy';

const shiftDate = (date: Date, unit: string, amount: number, op: 'add' | 'sub') => {
  if (unit === 'WEEKS') {
    return op === 'sub' ? subWeeks(date, amount) : addWeeks(date, amount);
  }
  if (unit === 'MONTHS') {
    return op === 'sub' ? subMonths(date, amount) : addMonths(date, amount);
  }
  return op === 'sub' ? subDays(date, amount) : addDays(date, amount);
};

export const formatDateValue = ({ value, dateFormat = defaultDateFormat }) => {
  if (value === undefined) {
    return '';
  }
  if (isDateRangeNotEmpty(value)) {
    return 'Is Not Empty';
  }

  if (value === null || isDateRangeEmpty(value)) {
    return 'Is Empty';
  }

  const relative = (() => {
    if (value?.relative && (value.relative.unit || value.relative.periodBehaviour)) {
      return value.relative;
    }
    if (value?.unit) {
      return value;
    }
    return null;
  })();

  if (relative) {
    const now = new Date();

    if (relative?.periodBehaviour) {
      const isRange = [PeriodBehavior.last, PeriodBehavior.previous].includes(relative.periodBehaviour);
      const start = isRange ? subDays(now, relative.value) : now;
      const end = isRange ? now : addDays(now, relative.value);
      return `${format(start, dateFormat)} - ${format(end, dateFormat)}`;
    }

    const { unit = 'DAYS', value: amount, direction } = relative;
    const isRange = direction === 'last' || direction === 'previous';
    const start = isRange ? shiftDate(now, unit, amount, 'sub') : now;
    const end = isRange ? now : shiftDate(now, unit, amount, 'add');
    return `${format(start, dateFormat)} - ${format(end, dateFormat)}`;
  }

  return isDateRangeValue(value)
    ? isEqual(new Date(value.from), new Date(epochDateString)) && value.to
      ? `Before ${format(add(new Date(value.to), { days: 1 }), dateFormat)}`
      : value.to
        ? `${format(new Date(value.from), dateFormat)} - ${format(new Date(value.to), dateFormat)}`
        : ''
    : '';
};

const emptyOperand = ['string', 'number', 'IP', 'FIX'].reduce(
  (obj, key) => ({
    ...obj,
    [key]: { operator: defaultOperator[ConditionType[key]], value: '' },
    [`${key}[]`]: { operator: `${defaultOperator[ConditionType[key]]}|ANY`, value: '' },
  }),
  {}
);

const findingStateOptions = [
  { title: 'Undetected', value: 'INACTIVE' },
  { title: 'Active', value: 'ACTIVE' },
];

const fieldsToIncludeMinutesAndHours = new Set(['alert.create_time', 'alert.first_seen']);
